import { useState } from 'react';
import { cn } from '@/shared/lib/cn';

const sizeClasses = {
  '2xs': 'size-5 text-[0.5625rem]',
  xs: 'size-6 text-[0.625rem]',
  sm: 'size-8 text-xs',
  md: 'size-10 text-sm',
  lg: 'size-12 text-base',
  xl: 'size-20 text-2xl',
} as const;

export interface AvatarProps {
  /** Adres zdjecia. Gdy go brak lub nie da sie wczytac, pokazujemy inicjaly. */
  src?: string | null;
  /** Imie i nazwisko — zrodlo inicjalow i tekstu alternatywnego. */
  name: string;
  size?: keyof typeof sizeClasses;
  /**
   * `cover` — kadrowanie do kola; dobre dla zdjec osob, gdzie liczy sie twarz.
   * `contain` — caly znak w ramce; dla logo klubu, ktore po przycieciu
   * traci czesc ksztaltu (logotypy rzadko sa kwadratowe).
   */
  fit?: 'cover' | 'contain';
  /**
   * Barwi inicjaly akcentem design systemu dobranym deterministycznie
   * z nazwiska — ta sama osoba ma wszedzie ten sam kolor. Zdjecia (gdy sa)
   * wygladaja identycznie jak bez tej opcji.
   */
  colorful?: boolean;
  className?: string;
}

/** Zwraca maksymalnie dwa inicjaly z imienia i nazwiska. */
function initialsOf(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toLocaleUpperCase('pl-PL'))
    .join('');
}

/**
 * Akcenty inicjalow — pelne klasy (Tailwind skanuje literaly), wzorzec
 * `bg-accent-*\/12 + text-accent-*` jak w kafelkach strony startowej.
 */
const ACCENT_CLASSES = [
  'bg-accent-blue/12 text-accent-blue',
  'bg-accent-purple/12 text-accent-purple',
  'bg-accent-green/12 text-accent-green',
  'bg-accent-orange/12 text-accent-orange',
  'bg-accent-teal/12 text-accent-teal',
  'bg-accent-coral/12 text-accent-coral',
  'bg-accent-yellow/12 text-accent-yellow',
  'bg-accent-pink/12 text-accent-pink',
  'bg-accent-magenta/12 text-accent-magenta',
  'bg-accent-lime/12 text-accent-lime',
] as const;

/** Stabilny akcent dla nazwiska — prosty hash sumujacy kody znakow. */
function accentOf(name: string): string {
  let hash = 0;
  for (const char of name) {
    hash = (hash * 31 + char.codePointAt(0)!) | 0;
  }
  return ACCENT_CLASSES[Math.abs(hash) % ACCENT_CLASSES.length]!;
}

/** Awatar uzytkownika ze zdjeciem albo inicjalami. */
export function Avatar({
  src,
  name,
  size = 'md',
  fit = 'cover',
  colorful = false,
  className,
}: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(src) && !failed;

  return (
    <span
      className={cn(
        'border-border relative inline-flex shrink-0',
        'items-center justify-center overflow-hidden rounded-full border font-semibold select-none',
        !showImage && colorful ? accentOf(name) : 'bg-surface-sunken text-fg-muted',
        sizeClasses[size],
        className,
      )}
    >
      {showImage ? (
        <img
          src={src ?? undefined}
          alt={name}
          loading="lazy"
          onError={() => setFailed(true)}
          className={cn(
            'size-full',
            fit === 'contain' ? 'object-contain p-1' : 'object-cover',
          )}
        />
      ) : (
        <span aria-hidden="true">{initialsOf(name)}</span>
      )}
      {!showImage && <span className="sr-only">{name}</span>}
    </span>
  );
}
